import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";

import { C, themeClasses } from "../../utils";
import type { ChatSession } from "../../../client/svcLearnerAi";

import { ChatSessionButton } from "./ChatSessionButton";

export interface ChatSessionSidebarProps {
  sessions: ChatSession[];
  selectedSessionId: string | null;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  streaming: boolean;
  onNewChat: () => void;
  onSelectSession: (sessionId: string) => void;
}

export function ChatSessionSidebar({
  sessions,
  selectedSessionId,
  loading,
  error,
  hasMore,
  streaming,
  onNewChat,
  onSelectSession,
}: ChatSessionSidebarProps) {
  return (
    <aside
      className={C(
        themeClasses.bgElevation1,
        "hidden lg:flex w-80 shrink-0 flex-col border-r border-surface-200 p-4 gap-3"
      )}
      aria-label="Chat sessions"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm">Chats</div>
          <div className="text-xs text-surface-500">Recent sessions</div>
        </div>
        <Button
          icon="pi pi-plus"
          label="New"
          size="small"
          onClick={onNewChat}
          disabled={streaming || !selectedSessionId}
          aria-label="Start new chat"
          data-testid="aspen-chat-new"
        />
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-surface-500 py-3">
          <ProgressSpinner style={{ width: "18px", height: "18px" }} strokeWidth="5" />
          Loading sessions...
        </div>
      )}
      {error && <Message severity="warn" text={error} className="w-full" />}
      {!loading && !error && sessions.length === 0 && (
        <div className="text-sm text-surface-500 py-3">No previous chats yet.</div>
      )}

      <div className={C("min-h-0 overflow-y-auto flex flex-col gap-2", themeClasses.scrollbar)}>
        {sessions.map(session => (
          <ChatSessionButton
            key={session.sessionId}
            session={session}
            selected={session.sessionId === selectedSessionId}
            disabled={streaming}
            onClick={() => onSelectSession(session.sessionId)}
          />
        ))}
      </div>
      {hasMore && (
        <div className="text-xs text-surface-400 pt-1">Showing the most recent sessions.</div>
      )}
    </aside>
  );
}
